// composables/useCartBridge.js
// Bridge unificado para "baterias" y "neumaticos" (Vuex en Nuxt 3)
// Objetivo: que los componentes NO conozcan el nombre real del módulo.
//
// Siempre úsalo dentro de <script setup> o de otro composable.

import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { NEGOCIOS } from "~/constants/negocios";

// Convierte lo que sea a arreglo de forma segura
const asegurarArreglo = (v) => (Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : []);

function nsCommit(store, ns, mutation, payload) {
  try {
    store.commit(`${ns}/${mutation}`, payload);
  } catch (e) {
    console.warn(`[useCartBridge] commit fallo: ${ns}/${mutation}`, e);
  }
}

async function nsDispatch(store, ns, action, payload) {
  try {
    return await store.dispatch(`${ns}/${action}`, payload);
  } catch (e) {
    console.warn(`[useCartBridge] dispatch fallo: ${ns}/${action}`, e);
    return undefined;
  }
}

function leerGetter(store, ns, nombre) {
  const ruta = `${ns}/${nombre}`;
  if (store.getters && Object.prototype.hasOwnProperty.call(store.getters, ruta)) {
    return store.getters[ruta];
  }
  return undefined;
}

// ────────────────────────────────────────────────────────────────
// Hook principal
// ────────────────────────────────────────────────────────────────
export const useCartBridge = (options = {}) => {
  const store = useStore();
  const modo_sistema = useState('modo_sistema');

  // 1️⃣ Resolver namespace inicial
  const resolverNsPorModo = (modo) => {
    if (modo === NEGOCIOS.BATERIAS) return "baterias";
    if (modo === NEGOCIOS.NEUMATICOS) return "neumaticos";
    return "neumaticos"; // fallback defensivo
  };

  const ns = ref(options.namespace || resolverNsPorModo(modo_sistema.value));
  // 2️⃣ Si NO forzamos namespace, que siga al modo_sistema automáticamente
  if (!options.namespace) {
    watch(
      () => modo_sistema.value,
      (nuevo) => {
        ns.value = resolverNsPorModo(nuevo);
      }
    );
  }

  // 3️⃣ API pública por si algún componente avanzado quiere forzarlo manualmente
  const switchNamespace = (nextNs) => {
    ns.value = nextNs;
  };

  const state = computed(() => store.state?.[ns.value] || {});

  // ───────── Proxies de lectura (incluye todo lo que usan tus componentes) ─────────

  const pasoapaso = computed(() => state.value.pasoapaso ?? 0);
  const acceptTerms = computed(() => state.value.acceptTerms ?? false);
  const acceptPolitica = computed(() => state.value.acceptPolitica ?? false);
  const shoppingStep = computed(() => state.value.shoppingStep ?? 0);
  const ultimo_paso = computed(() => state.value.ultimo_paso ?? 0);

  const ubication = computed(
    () =>
      state.value.ubication ?? {
        isLoading: false,
        latitude: null,
        longitude: null,
        address: "",
        region: "",
        commune: "",
      }
  );

  const ubication_confirmed = computed(() => state.value.ubication_confirmed ?? {});

  const fullcercano = computed(() => state.value.fullcercano ?? {});

  const localpivote = computed(() => state.value.localpivote ?? {});

  const local_cambio = computed(() => state.value.local_cambio ?? false);

  const removedProducts = computed(() => state.value.removedProducts ?? false);

  const calendar = computed(
    () =>
      state.value.calendar ?? {
        checkStoreAvailability: false,
        scheduleType: 1,
        date: new Date().toISOString().slice(0, 10),
        time: null,
        totalTime: 0,
        tempario: "",
      }
  );

  const addresses = computed(
    () =>
      state.value.addresses ?? {
        showAddressOptions: false,
        showLeaveAddress: false,
        address: {
          region: null,
          commune: null,
          address: "",
          details: "",
          comments: "",
          latitude: null,
          longitude: null,
        },
        leaveAddress: {
          region: null,
          commune: null,
          address: "",
          details: "",
          comments: "",
          latitude: null,
          longitude: null,
        },
      }
  );

  const personalInformation = computed(
    () =>
      state.value.personalInformation ?? {
        rut: "",
        name: "",
        lastName: "",
        surName: "",
        email: "",
        phone: "",
        carplate: "",
        patente_pais: "",
      }
  );

  const personalInfoSaved = computed(() => state.value.personalInfoSaved ?? false);

  const payment = computed(
    () =>
      state.value.payment ?? {
        paymentId: "",
        paymentMethod: "",
      }
  );

  const products = computed(() => {
    const raw = state.value?.products ?? [];
    const normalizados = asegurarArreglo(raw);
    return normalizados;
  });

  const isLoadingLocation = computed(() => !!ubication.value?.isLoading);

  const localSeleccionado = computed(() => state.value.localSeleccionado ?? null);

  const batteryInstallType = computed(() => state.value.batteryInstallType ?? state.value.install?.batteryInstallType ?? null);

  const gifts = computed(() => state.value.gifts ?? []);

  const regalo_ruleta = computed(() => state.value.regalo_ruleta ?? []);

  const isValidCarplate = computed(() => state.value.isValidCarplate ?? []);

  const giftcard = computed(() => state.value.giftcard ?? []);

  const giftcardInput = computed(() => state.value.giftcardInput ?? []);

  const services = computed(() => state.value.services ?? []);

  const scheduleType = computed(() => calendar.value.scheduleType ?? 1);

  const transaction_uuid = computed(() => state.value.transaction_uuid ?? []);

  // ───────── Init / timers ─────────

  const verifyCart = () => {
    nsCommit(store, ns.value, "verifyCartTimestamp");
  };

  const startExpirationTimer = () => {
    nsCommit(store, ns.value, "startExpirationTimer");
  };

  // ───────── Cart (items) ─────────

  const addProducts = async (product, quantity = 1) => {
    if (!product || product.codigo == null) {
      console.warn("[useCartBridge] addItem: product inválido (sin codigo)");
      return;
    }

    if (ns.value === "baterias") {
      await nsDispatch(store, ns.value, "addItemAndStart", { product, quantity });
    } else {
      nsCommit(store, ns.value, "addProducts", {
        selectedProduct: product,
        selectQuantity: quantity,
      });
      verifyCart();
    }
  };

  const replaceItem = async (product, quantity = 1) => {
    if (!product) return;

    if (ns.value === "baterias") {
      await nsDispatch(store, ns.value, "replaceItem", { product, quantity });
    } else {
      nsCommit(store, ns.value, "removeAllProducts");
      nsCommit(store, ns.value, "addProducts", {
        selectedProduct: product,
        selectQuantity: quantity,
      });
      verifyCart();
    }
  };

  const updatePriceAndMarkup = (codigo, precio, markup) => {
    nsCommit(store, ns.value, "updateProductPrice", {
      codigo,
      // compatibilidad neumáticos
      precio_venta_web: precio,
      // compatibilidad baterías
      precio_venta: precio,
      markup,
    });
  };

  const getProducts = () => nsCommit(store, ns.value, "getProducts");

  const verifyCartTimestamp = () => nsCommit(store, ns.value, "verifyCartTimestamp");

  // relee el timestamp desde localStorage y lo devuelve como numero (null si no hay)
  const getCartTimestamp = () => {
    nsCommit(store, ns.value, "getCartTimestamp");
    const valor = state.value.cartTimestamp;
    if (valor === null || valor === undefined) {
      return null;
    }
    return Number(valor);
  };

  // codigo del carrito en el backend (null si todavia no se crea)
  const getCarritoCodigo = () => {
    nsCommit(store, ns.value, "getCarritoCodigo");
    const codigo = state.value.carrito_codigo ?? null;
    return codigo;
  };

  const setCarritoCodigo = (codigo) => nsCommit(store, ns.value, "setCarritoCodigo", codigo);

  const getAcceptTerms = () => nsCommit(store, ns.value, "getAcceptTerms");

  const getAcceptPolitica = () => nsCommit(store, ns.value, "getAcceptPolitica");

  const removeByCode = (codigo) => nsCommit(store, ns.value, "removeProductByCode", codigo);

  const removeProduct = (product) => nsCommit(store, ns.value, "removeProduct", product);

  const clearCart = () => nsCommit(store, ns.value, "removeAllProducts");

  const clearAll = () => {
    if (ns.value === "baterias") {
      nsCommit(store, ns.value, "RESET_ALL");
    } else {
      nsCommit(store, ns.value, "removeAllProducts");
      nsCommit(store, ns.value, "removeAllRemovedProducts");
      nsCommit(store, ns.value, "removeAllServices");
      nsCommit(store, ns.value, "setCalendarToDefault", 1);
      nsCommit(store, ns.value, "removeGiftcard");
      nsCommit(store, ns.value, "resetGifts");
      nsCommit(store, ns.value, "resetRegaloRuleta");
      nsCommit(store, ns.value, "removeAllLocalStorages");
    }
  };

  const getRemoved = () => nsCommit(store, ns.value, "getRemovedProducts");

  const getPayment = () => nsCommit(store, ns.value, "getPayment");

  const clearRemoved = () => nsCommit(store, ns.value, "removeAllRemovedProducts");

  const setQuantity = (product, quantity) => {
    nsCommit(store, ns.value, "setProductsQuantity", { product, quantity });
  };

  const getShoppingStep = () => {
    nsCommit(store, ns.value, "getShoppingStep");
  };

  const getUltimoPaso = () => {
    nsCommit(store, ns.value, "getUltimo_paso");
  };
  // ───────── Métricas ─────────

  const countDistinct = computed(() => {
    const g = leerGetter(store, ns.value, "getCountDistinct");
    return g != null ? g : items.value.length;
  });

  const countUnits = computed(() => {
    const g = leerGetter(store, ns.value, "getCountUnits");
    if (g != null) return g;
    return items.value.reduce((t, i) => t + (Number(i?.quantity ?? 0) || 0), 0);
  });

  const isExpired = computed(() => {
    const g = leerGetter(store, ns.value, "isExpired");
    return g != null ? g : false;
  });

  // ───────── pasos / meta ─────────

  const setAcceptTerms = (payload) => nsCommit(store, ns.value, "setAcceptTerms", payload);
  
  const setAcceptPolitica = (value) => nsCommit(store, ns.value, "setAcceptPolitica", value);


  const setPasoAPaso = (paso) => nsCommit(store, ns.value, ns.value === "baterias" ? "SET_PASOAPASO" : "setPasoAPaso", paso);

  const setShoppingStep = (step) => nsCommit(store, ns.value, "setShoppingStep", step);

  const setUltimoPaso = (ultimo) => nsCommit(store, ns.value, "setUltimo_paso", ultimo);

  const setTransactionUUID = (uuid) => {
    if (ns.value === "baterias") {
      nsCommit(store, ns.value, "SET_TRANSACTION_UUID", uuid);
    } else {
      nsCommit(store, ns.value, "settransaction_uuid", uuid);
    }
  };

  const setIsValidCarplate = (valid) => {
    nsCommit(store, ns.value, "SET_IS_VALID_CARPLATE", !!valid);
  };

  const getPasoAPaso = () => {
    try {
      const valor = localStorage.getItem("pasoapaso") || 0;
      nsCommit(store, ns.value, "setPasoAPaso", Number(valor));
      return Number(valor);
    } catch (e) {
      console.warn("[useCartBridge] getPasoAPaso() fallo:", e);
      return 0;
    }
  };

  // ───────── Install / store selection ─────────

  const setLocalSeleccionado = (local) => {
    //console.log("setLocalSeleccionado: local", local);
    nsCommit(store, ns.value, "setlocalSeleccionado", local || null);
  };

  // ───────── Calendar ─────────
  const getCalendar = () => nsCommit(store, ns.value, "getCalendar");

  const setCalendarToDefault = (scheduleType = 1) => nsCommit(store, ns.value, "setCalendarToDefault", scheduleType);

  const setCheckStoreAvailability = (check, scheduleType = 1) =>
    nsCommit(store, ns.value, "setCheckStoreAvailability", {
      checkStoreAvailability: check,
      scheduleType,
    });

  const setScheduleType = (scheduleType = 1) => nsCommit(store, ns.value, "setScheduleType", scheduleType);

  const setDate = (date, scheduleType = 1) => nsCommit(store, ns.value, "setDate", { date, scheduleType });

  const setTime = (time, scheduleType = 1) => nsCommit(store, ns.value, "setTime", { time, scheduleType });

  const setTotalTime = (totalTime, scheduleType = 1) => nsCommit(store, ns.value, "setTotalTime", { totalTime, scheduleType });

  const setTempario = (tempario, scheduleType = 1) => nsCommit(store, ns.value, "setTempario", { tempario, scheduleType });

  // ───────── Address / addresses ─────────

  const setAddress = (payload) => nsCommit(store, ns.value, "setAddress", payload);

  const setAddressesFlags = (opts = {}) => {
    if (opts.showAddressOptions != null) {
      nsCommit(store, ns.value, "setShowAddressOptions", !!opts.showAddressOptions);
    }
    if (opts.showLeaveAddress != null) {
      nsCommit(store, ns.value, "setShowLeaveAddress", !!opts.showLeaveAddress);
    }
  };

  const setAddressRegion = (region, type = "address") => nsCommit(store, ns.value, "setAddressRegion", { region, type });

  const setAddressCommune = (commune, type = "address") => nsCommit(store, ns.value, "setAddressCommune", { commune, type });

  const setAddressAddress = (address, type = "address") => nsCommit(store, ns.value, "setAddressAddress", { address, type });

  const setAddressDetails = (details, type = "address") => nsCommit(store, ns.value, "setAddressDetails", { details, type });

  const setAddressLatitudeAndLongitude = (latitude, longitude, type = "address") =>
    nsCommit(store, ns.value, "setAddressLatitudeAndLongitude", {
      latitude,
      longitude,
      type,
    });

  const setAddressComments = (comments, type = "address") => nsCommit(store, ns.value, "setAddressComments", { comments, type });

  const refreshAddressWasChanged = () => nsCommit(store, ns.value, "getaAddressWasChanged");

  const setRegion = (region) => nsCommit(store, ns.value, "setRegion", region);

  const setCommune = (commune) => nsCommit(store, ns.value, "setCommune", commune);

  const getAddresses = () => nsCommit(store, ns.value, "getAddresses");

  const setShowAddressOptions = (show) => nsCommit(store, ns.value, "setShowAddressOptions", !!show);

  const setShowLeaveAddress = (show) => nsCommit(store, ns.value, "setShowLeaveAddress", !!show);

  const setLeaveAddress = (leaveAddress) => nsCommit(store, ns.value, "setLeaveAddress", leaveAddress);
  // ───────── Datos personales ─────────

  const setPersonalInformation = (payload) => nsCommit(store, ns.value, "setPersonalInformation", payload);
  
  const setPersonalInfoSaved = (payload) => nsCommit(store, ns.value, "setPersonalInfoSaved", payload);

  const getPersonalInformation = () => nsCommit(store, ns.value, "getPersonalInformation");

  // ───────── Payment ─────────

  const setPayment = (paymentData) => nsCommit(store, ns.value, "setPayment", paymentData);
  
  const gettransaction_uuid = () => nsCommit(store, ns.value, "gettransaction_uuid");

  // ───────── Services ─────────

  const getServicesStore = () => nsCommit(store, ns.value, "getServicesStore");

  const addService = (service) => nsCommit(store, ns.value, "addService", service);

  const removeService = (service) => nsCommit(store, ns.value, "removeService", service);

  const removeAllServices = () => nsCommit(store, ns.value, "removeAllServices");

  // ───────── Giftcard / Gifts ─────────

  const setGiftcard = (giftcard) => nsCommit(store, ns.value, "setGiftcard", giftcard);

  const getGiftcard = () => nsCommit(store, ns.value, "getGiftcard");

  const removeGiftcard = () => nsCommit(store, ns.value, "removeGiftcard");

  const getGifts = () => nsCommit(store, ns.value, "getGifts");
  const getRegaloRuleta = () => nsCommit(store, ns.value, "getRegaloRuleta");

  const setGifts = (gifts) => nsCommit(store, ns.value, "setGifts", gifts);
  const setRegaloRuleta = (regalo) => nsCommit(store, ns.value, "setRegaloRuleta", regalo);

  const resetGifts = () => nsCommit(store, ns.value, "resetGifts");
  const resetRegaloRuleta = () => nsCommit(store, ns.value, "resetRegaloRuleta");
  
  const setGiftcardLoader = (value = false) => {
    try {
      nsCommit(store, ns.value, "set_giftcardLoader", !!value);
    } catch (e) {
      console.warn("[useCartBridge] setGiftcardLoader() fallo:", e);
    }
  };

  const setgiftcardInput = (value) => {
    try {
      nsCommit(store, ns.value, "setgiftcardInput", value);
    } catch (e) {
      console.warn("[useCartBridge] setGiftcardLoader() fallo:", e);
    }
  };

  const resetGiftcardInput = () => {
    try {
      nsCommit(store, ns.value, "resetGiftcardInput");
    } catch (e) {
      console.warn("[useCartBridge] setGiftcardLoader() fallo:", e);
    }
  };
  // ───────── Ubicación helpers ─────────

  const setUbication = (ubi) => nsCommit(store, ns.value, "setUbication", ubi);

  const setUbicationAddress = (address) => nsCommit(store, ns.value, "setUbicationAddress", address);

  const setFullcercano = (local) => nsCommit(store, ns.value, "setFullcercano", local);

  const setUbicationConfirmed = (ubi) => nsCommit(store, ns.value, "setUbication_confirmed", ubi);

  const getUbication = () => nsCommit(store, ns.value, "getUbication");

  const getUbicationConfirmed = () => nsCommit(store, ns.value, "getUbication_confirmed");

  const getFullcercano = () => nsCommit(store, ns.value, "getFullcercano");

  // ───────── Local storage cleanup ─────────

  const removeAllLocalStorages = () => nsCommit(store, ns.value, "removeAllLocalStorages");

  const setLocalCambio = (value) => {
    nsCommit(store, ns.value, "setLocal_cambio", value);
  };
  const setLocalPivote = (value) => {
    nsCommit(store, ns.value, "setLocalPivote", value);
  };
  const getlocalSeleccionado = () => {
    nsCommit(store, ns.value, "getlocalSeleccionado");
  };
  const getLocalPivote = () => {
    nsCommit(store, ns.value, "getLocalPivote");
  };

  const getLocalCambio = () => {
    nsCommit(store, ns.value, "getLocal_cambio");
  };

  // ───────── Baterias ─────────

  const getBatteryInstallType = () => {
    //const valor = localStorage.getItem("batteryInstallType") || null
    nsCommit(store, ns.value, "getBatteryInstallType");
  };
  const setBatteryInstallType = (valor) => {
    nsCommit(store, ns.value, "setBatteryInstallType", valor);
  };

  // ───────── Expuestos ─────────

  return {
    // namespace / estado bruto
    ns,
    state,
    switchNamespace,

    // proxies de estado
    pasoapaso,
    acceptTerms,
    acceptPolitica,
    shoppingStep,
    ultimo_paso,
    ubication,
    ubication_confirmed,
    fullcercano,
    localpivote,
    local_cambio,
    calendar,
    addresses,
    personalInformation,
    personalInfoSaved,
    payment,
    products,
    isLoadingLocation,
    localSeleccionado,
    batteryInstallType,
    gifts,
    regalo_ruleta,
    isValidCarplate,
    giftcard,
    giftcardInput,
    localpivote,
    scheduleType,
    transaction_uuid,
    removedProducts,
    getAcceptPolitica,
    setAcceptPolitica,

    // timers
    verifyCart,
    startExpirationTimer,

    // cart
    addProducts,
    replaceItem,
    updatePriceAndMarkup,
    removeByCode,
    removeProduct,
    clearCart,
    clearAll,
    getRemoved,
    clearRemoved,
    setQuantity,

    // métricas
    countDistinct,
    countUnits,
    isExpired,
    getProducts,
    verifyCartTimestamp,

    // pasos
    setPasoAPaso,
    setShoppingStep,
    setUltimoPaso,
    setTransactionUUID,
    setIsValidCarplate,
    getPasoAPaso,

    // install / store
    setLocalSeleccionado,
    getShoppingStep,
    getUltimoPaso,

    // calendar
    getCalendar,
    setCalendarToDefault,
    setCheckStoreAvailability,
    setScheduleType,
    setDate,
    setTime,
    setTotalTime,
    setTempario,

    // address
    setAddress,
    setAddressesFlags,
    setAddressRegion,
    setAddressCommune,
    setAddressAddress,
    setAddressDetails,
    setAddressLatitudeAndLongitude,
    setAddressComments,
    refreshAddressWasChanged,
    setRegion,
    setCommune,
    getAddresses,
    setShowAddressOptions,
    setShowLeaveAddress,
    setLeaveAddress,

    // personal
    setPersonalInformation,
    getPersonalInformation,
    setPersonalInfoSaved,

    // payment
    setPayment,
    gettransaction_uuid,
    getPayment,

    // services
    services,
    getServicesStore,
    addService,
    removeService,
    removeAllServices,

    // regalitos
    setGiftcard,
    getGiftcard,
    removeGiftcard,
    getGifts,
    getRegaloRuleta,
    setGifts,
    setRegaloRuleta,
    resetGifts,
    resetRegaloRuleta,
    setGiftcardLoader,

    // ubicación helpers
    setUbication,
    setUbicationAddress,
    setUbicationConfirmed,
    setFullcercano,
    getUbication,
    getUbicationConfirmed,
    getFullcercano,
    getCartTimestamp,
    getCarritoCodigo,
    setCarritoCodigo,

    // storages
    removeAllLocalStorages,
    getlocalSeleccionado,
    setLocalCambio,
    setLocalPivote,
    getLocalPivote,
    getLocalCambio,

    //Baterias
    getBatteryInstallType,
    setBatteryInstallType,

    getAcceptTerms,
    setAcceptTerms,
    setgiftcardInput,
    resetGiftcardInput,
  };
};
